import { useMemo, useState } from 'react';
import { Boxes, Download, Printer, RefreshCw, XCircle } from 'lucide-react';
import { usePackingOpenPos, useConsolidatedPo } from '@/hooks/use-packing';
import { useLocationStore } from '@/stores/location-store';
import { formatDateTime } from '@/lib/utils';
import { api } from '@/lib/api-client';
import { toast } from '@/stores/toast-store';

// Warehouse packing view: merge open POs from every non-warehouse location
// into one read-only consolidated PO. The packing team confirms which POs to
// include (all open POs are pre-selected), generates the merged product list,
// and prints/downloads it. Quantities are never edited here and the source
// POs are untouched — this surface exists purely for picking and packing.
export function PackingConsolidatedPage() {
  const { selectedLocationId } = useLocationStore();
  const { data, isLoading, refetch, isRefetching } = usePackingOpenPos(selectedLocationId);
  const openPos = useMemo(() => data?.data ?? [], [data]);

  // Every open PO starts selected; we track the ids the user REMOVED so no
  // effect is needed to sync state when the list loads or refetches — newly
  // arriving POs are selected by default.
  const [removedIds, setRemovedIds] = useState<Set<string>>(new Set());
  // Ids captured when the user hits "Generate" — a snapshot, so toggling
  // checkboxes afterwards doesn't silently change the generated document.
  const [confirmedIds, setConfirmedIds] = useState<string[] | null>(null);

  const selectedIds = useMemo(
    () => new Set(openPos.filter((po) => !removedIds.has(po.id)).map((po) => po.id)),
    [openPos, removedIds],
  );

  const consolidated = useConsolidatedPo(selectedLocationId, confirmedIds);
  const merged = consolidated.data?.data;

  function toggle(id: string) {
    setRemovedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
    setConfirmedIds(null);
  }

  function toggleAll() {
    setRemovedIds(selectedIds.size === openPos.length ? new Set(openPos.map((po) => po.id)) : new Set());
    setConfirmedIds(null);
  }

  function handlePrint() {
    if (!confirmedIds || !selectedLocationId) return;
    const ids = confirmedIds.join(',');
    // Named tab (same pattern as the PO print page) so repeated clicks reuse
    // one tab instead of spawning new ones.
    window.open(
      `#/pos-management/packing/print?loc=${selectedLocationId}&ids=${ids}`,
      `packing-print-${selectedLocationId}`,
    );
  }

  const [downloading, setDownloading] = useState(false);
  async function handleDownloadPdf() {
    if (!confirmedIds || !selectedLocationId || downloading) return;
    setDownloading(true);
    try {
      const blob = await api.postBlob('/purchase-orders/packing/consolidated/pdf', {
        warehouseLocationId: selectedLocationId,
        poIds: confirmedIds,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `packing-list-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Could not download the packing-list PDF. A selected order may no longer be open.');
    } finally {
      setDownloading(false);
    }
  }

  if (isLoading) {
    return <div className="flex h-64 items-center justify-center text-muted-foreground">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-semibold text-foreground">
            <Boxes className="h-5 w-5 text-primary" />
            Consolidated PO — Packing
          </h2>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Merge open purchase orders from all store locations into a single packing list. Untick any PO you don't want
            included, then generate.
          </p>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isRefetching}
          className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm font-medium hover:bg-muted"
        >
          <RefreshCw className={`h-4 w-4 ${isRefetching ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {openPos.length === 0 ? (
        <div className="rounded-xl border border-border bg-white p-10 text-center text-sm text-muted-foreground">
          No open purchase orders from store locations right now.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs font-semibold uppercase text-muted-foreground">
                <th className="w-10 px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selectedIds.size === openPos.length && openPos.length > 0}
                    onChange={toggleAll}
                    className="h-4 w-4 accent-primary"
                  />
                </th>
                <th className="px-4 py-3">PO #</th>
                <th className="px-4 py-3">Location</th>
                <th className="px-4 py-3">Vendor</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Items</th>
                <th className="px-4 py-3">Created</th>
              </tr>
            </thead>
            <tbody>
              {openPos.map((po) => (
                <tr
                  key={po.id}
                  onClick={() => toggle(po.id)}
                  className={`cursor-pointer border-b border-border last:border-0 hover:bg-muted/40 ${
                    selectedIds.has(po.id) ? '' : 'opacity-50'
                  }`}
                >
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(po.id)}
                      onChange={() => toggle(po.id)}
                      className="h-4 w-4 accent-primary"
                    />
                  </td>
                  <td className="px-4 py-3 font-medium">#{po.orderNumber}</td>
                  <td className="px-4 py-3">{po.locationName}</td>
                  <td className="px-4 py-3">{po.payeeName ?? '—'}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize ${
                        po.status === 'sent' ? 'bg-blue-100 text-blue-800' : 'bg-slate-100 text-slate-700'
                      }`}
                    >
                      {po.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">{po.itemCount}</td>
                  <td className="px-4 py-3 text-muted-foreground">{formatDateTime(po.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {openPos.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-muted-foreground">
            {selectedIds.size} of {openPos.length} purchase orders selected
          </p>
          <button
            onClick={() => setConfirmedIds([...selectedIds])}
            disabled={selectedIds.size === 0 || consolidated.isFetching}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {consolidated.isFetching ? 'Generating…' : 'Generate Consolidated PO'}
          </button>
        </div>
      )}

      {consolidated.isError && confirmedIds && (
        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          <XCircle className="h-4 w-4 shrink-0" />
          Couldn't generate the consolidated PO. A selected order may no longer be open — refresh the list and try
          again.
        </div>
      )}

      {merged && confirmedIds && (
        <div className="space-y-4 rounded-xl border border-border bg-white p-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-base font-semibold text-foreground">
                Consolidated PO · {merged.pos.length} orders · {merged.totalProducts} products
              </h3>
              <p className="text-xs text-muted-foreground">
                Generated {formatDateTime(merged.generatedAt)} for {merged.warehouseLocationName}. Read-only — source
                POs are not modified.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleDownloadPdf}
                disabled={downloading}
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              >
                <Download className="h-4 w-4" />
                {downloading ? 'Preparing…' : 'Download PDF'}
              </button>
              <button
                onClick={handlePrint}
                className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-muted"
              >
                <Printer className="h-4 w-4" />
                Print
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-border text-left text-xs font-semibold uppercase text-muted-foreground">
                  <th className="py-2 pr-4">Product</th>
                  <th className="py-2 pr-4">Unit</th>
                  <th className="py-2 pr-4 text-right">Total Qty</th>
                  <th className="py-2">Per Location</th>
                </tr>
              </thead>
              <tbody>
                {merged.items.map((item) => (
                  <tr key={`${item.productId}-${item.unitOfMeasure}`} className="border-b border-border last:border-0">
                    <td className="py-2 pr-4">
                      <div className="font-medium">{item.productName}</div>
                      {item.productBarcode && (
                        <div className="text-xs text-muted-foreground">{item.productBarcode}</div>
                      )}
                    </td>
                    <td className="py-2 pr-4">{item.unitOfMeasure}</td>
                    <td className="py-2 pr-4 text-right font-semibold">{item.totalQuantity}</td>
                    <td className="py-2 text-xs text-muted-foreground">
                      {item.breakdown.map((b) => (
                        <span
                          key={`${b.poId}-${b.locationId}`}
                          className="mb-1 mr-2 inline-flex rounded-full bg-muted px-2 py-0.5"
                        >
                          {b.locationName}: {b.quantity} (PO #{b.orderNumber})
                        </span>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
